/**
 * SDK/CLI API Client
 */

import { api } from '@/lib/api-client';
import type {
  SDKApiKey,
  CreateSDKKeyInput,
  SDKKeyResponse,
  SDKKeyCreatedResponse,
  SDKKeyListResponse,
  SDKKeyUsageResponse,
  CLISession,
  CLISessionResponse,
} from './types';

const SDK_BASE = '/api/v1/sdk-keys';
const CLI_BASE = '/api/v1/cli';

// ============================================
// SDK API Keys
// ============================================

/**
 * List SDK API keys
 */
export async function listSDKKeys(): Promise<SDKKeyListResponse> {
  return api.get<SDKKeyListResponse>(SDK_BASE);
}

/**
 * Get SDK key by ID
 */
export async function getSDKKey(id: string): Promise<SDKKeyResponse> {
  return api.get<SDKKeyResponse>(`${SDK_BASE}/${id}`);
}

/**
 * Create a new SDK API key
 */
export async function createSDKKey(input: CreateSDKKeyInput): Promise<SDKKeyCreatedResponse> {
  return api.post<SDKKeyCreatedResponse>(SDK_BASE, input);
}

/**
 * Update SDK key
 */
export async function updateSDKKey(
  id: string,
  input: Partial<CreateSDKKeyInput>
): Promise<SDKKeyResponse> {
  return api.patch<SDKKeyResponse>(`${SDK_BASE}/${id}`, input);
}

/**
 * Revoke (delete) SDK key
 */
export async function revokeSDKKey(id: string): Promise<{ success: boolean }> {
  return api.delete(`${SDK_BASE}/${id}`);
}

/**
 * Rotate SDK key (generates new key, returns full key once)
 */
export async function rotateSDKKey(id: string): Promise<SDKKeyCreatedResponse> {
  return api.post<SDKKeyCreatedResponse>(`${SDK_BASE}/${id}/rotate`);
}

/**
 * Get SDK key usage statistics
 */
export async function getSDKKeyUsage(
  id: string,
  days: number = 30
): Promise<SDKKeyUsageResponse> {
  return api.get<SDKKeyUsageResponse>(`${SDK_BASE}/${id}/usage`, {
    params: { days: String(days) },
  });
}

// ============================================
// CLI Device Auth
// ============================================

/**
 * Get CLI session by user code (for web authorization page)
 */
export async function getCLISession(userCode: string): Promise<CLISessionResponse> {
  return api.get<CLISessionResponse>(`${CLI_BASE}/device/status`, {
    params: { user_code: userCode },
  });
}

/**
 * Authorize CLI device
 */
export async function authorizeCLIDevice(userCode: string): Promise<CLISessionResponse> {
  return api.post<CLISessionResponse>(`${CLI_BASE}/device/authorize`, {
    user_code: userCode,
  });
}

/**
 * Deny CLI device
 */
export async function denyCLIDevice(userCode: string): Promise<CLISessionResponse> {
  return api.post<CLISessionResponse>(`${CLI_BASE}/device/deny`, {
    user_code: userCode,
  });
}

// ============================================
// Export all functions
// ============================================

export default {
  // SDK Keys
  listSDKKeys,
  getSDKKey,
  createSDKKey,
  updateSDKKey,
  revokeSDKKey,
  rotateSDKKey,
  getSDKKeyUsage,
  // CLI
  getCLISession,
  authorizeCLIDevice,
  denyCLIDevice,
};
